"use client";
import * as React from "react";
import { motion } from "framer-motion";
import { SparklesIcon, ArrowDownIcon } from '@heroicons/react/24/outline';

export default function Hero() {
  const ANIM_DURATION = 0.8;
  const ANIM_EASING = [0.25, 0.46, 0.45, 0.94];
  
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    show: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: ANIM_DURATION, ease: ANIM_EASING } 
    }
  };

  const fadeInDown = {
    hidden: { opacity: 0, y: -30 },
    show: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: ANIM_DURATION, ease: ANIM_EASING, delay: 0.2 } 
    }
  };

  const fadeInScale = {
    hidden: { opacity: 0, scale: 0.8 },
    show: { 
      opacity: 1, 
      scale: 1, 
      transition: { duration: ANIM_DURATION, ease: ANIM_EASING, delay: 0.4 } 
    }
  };

  // Animation pour les mots qui apparaissent un par un
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const wordVariants = {
    hidden: { 
      opacity: 0,
      y: 50,
      scale: 0.8
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  // Animation de brillance
  const shineVariants = {
    hidden: { x: '-100%' },
    visible: { 
      x: '200%',
      transition: {
        duration: 2,
        ease: 'easeInOut',
        delay: 2,
        repeat: Infinity,
        repeatDelay: 5
      }
    }
  };

  const titleWords = [
    { text: "Révolutionnez", color: "text-gray-900" },
    { text: "votre", color: "text-gray-900" },
    { text: "RH", color: "bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent" },
    { text: "avec", color: "text-gray-900" },
    { text: "l'IA", color: "bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent" }
  ];

  return (
    <section id="hero" className="relative min-h-screen bg-gradient-to-br from-white to-violet-50/20 flex items-center justify-center overflow-hidden pt-28 sm:pt-24 smooth-scroll-section">
      {/* Système de particules violettes animées */}
      <div className="absolute inset-0">
        {/* Particules flottantes - différentes tailles et animations */}
        <div className="absolute top-10 left-10 w-3 h-3 bg-violet-400/60 rounded-full animate-bounce" style={{ animationDuration: '2s', animationDelay: '0s' }}></div>
        <div className="absolute top-20 right-20 w-2 h-2 bg-purple-500/50 rounded-full animate-ping" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute top-32 left-1/4 w-1.5 h-1.5 bg-violet-600/40 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-44 right-1/3 w-4 h-4 bg-purple-400/30 rounded-full animate-bounce" style={{ animationDuration: '3s', animationDelay: '1.5s' }}></div>
        
        <div className="absolute top-60 left-16 w-2.5 h-2.5 bg-violet-500/45 rounded-full animate-ping" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-80 right-16 w-1 h-1 bg-purple-600/60 rounded-full animate-pulse" style={{ animationDelay: '2.5s' }}></div>
        <div className="absolute top-96 left-1/3 w-3.5 h-3.5 bg-violet-400/35 rounded-full animate-bounce" style={{ animationDuration: '2.5s', animationDelay: '3s' }}></div>
        
        {/* Particules au centre */}
        <div className="absolute top-1/2 left-20 w-2 h-2 bg-purple-500/40 rounded-full animate-ping" style={{ animationDelay: '0.8s' }}></div>
        <div className="absolute top-1/2 right-24 w-1.5 h-1.5 bg-violet-600/50 rounded-full animate-pulse" style={{ animationDelay: '1.2s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-3 h-3 bg-purple-400/25 rounded-full animate-bounce" style={{ animationDuration: '4s', animationDelay: '1.8s' }}></div>
        
        {/* Particules en bas */}
        <div className="absolute bottom-20 left-12 w-2.5 h-2.5 bg-violet-500/55 rounded-full animate-ping" style={{ animationDelay: '0.3s' }}></div>
        <div className="absolute bottom-32 right-12 w-1.5 h-1.5 bg-purple-600/45 rounded-full animate-pulse" style={{ animationDelay: '0.7s' }}></div>
        <div className="absolute bottom-44 left-1/4 w-4 h-4 bg-violet-400/30 rounded-full animate-bounce" style={{ animationDuration: '3.5s', animationDelay: '1.1s' }}></div>
        <div className="absolute bottom-60 right-1/4 w-2 h-2 bg-purple-500/40 rounded-full animate-ping" style={{ animationDelay: '1.4s' }}></div>
        <div className="absolute bottom-80 left-1/3 w-1 h-1 bg-violet-600/60 rounded-full animate-pulse" style={{ animationDelay: '1.7s' }}></div>
        <div className="absolute bottom-96 right-1/3 w-3 h-3 bg-purple-400/35 rounded-full animate-bounce" style={{ animationDuration: '2.8s', animationDelay: '2.1s' }}></div>
        
        {/* Particules supplémentaires pour plus de richesse */}
        <div className="absolute top-1/4 left-1/6 w-1.5 h-1.5 bg-violet-500/50 rounded-full animate-ping" style={{ animationDelay: '2.3s' }}></div>
        <div className="absolute top-3/4 right-1/6 w-2.5 h-2.5 bg-purple-400/40 rounded-full animate-pulse" style={{ animationDelay: '2.7s' }}></div>
        <div className="absolute top-1/3 right-1/5 w-3.5 h-3.5 bg-violet-600/25 rounded-full animate-bounce" style={{ animationDuration: '3.2s', animationDelay: '3.1s' }}></div>
        <div className="absolute bottom-1/3 left-1/5 w-2 h-2 bg-purple-500/45 rounded-full animate-ping" style={{ animationDelay: '3.4s' }}></div>
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="max-w-4xl mx-auto">
          {/* Badge animé */}
          <motion.div
            variants={fadeInDown}
            initial="hidden"
            animate="show"
            className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm border border-violet-200/60 rounded-full px-3 py-1.5 sm:px-4 sm:py-2 mb-6 sm:mb-8 shadow-lg"
          >
            <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-r from-violet-500 to-purple-600 rounded-full flex items-center justify-center">
              <SparklesIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
            </div>
            <span className="text-xs sm:text-sm font-medium text-gray-700">
              Solution IA spécialisée en Ressources Humaines
            </span>
          </motion.div>

          {/* Titre principal avec effet dynamique */}
          <motion.div
            className="mb-6 relative"
            initial="hidden"
            animate="visible"
          >
            {/* Titre avec animation de mots */}
            <motion.h1
              variants={containerVariants}
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold mb-4 leading-tight relative overflow-hidden"
            >
              {titleWords.map((word, index) => (
                <motion.span
                  key={index}
                  variants={wordVariants}
                  style={{ display: 'inline-block', marginRight: '0.5rem' }}
                  className={`${word.color} font-semibold relative`}
                >
                  {word.text}
                </motion.span>
              ))}
              
              {/* Effet de brillance qui passe sur le titre */}
              <motion.div
                variants={shineVariants}
                initial="hidden"
                animate="visible"
                className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"
                style={{
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                  transform: 'skewX(-45deg)'
                }}
              />
            </motion.h1>

            {/* Sous-titre animé avec effet de pulsation */}
            <motion.div
              variants={containerVariants}
              className="text-xl sm:text-2xl text-gray-600 leading-relaxed"
            >
              <motion.span
                variants={wordVariants}
                className="inline-block mr-2"
              >
                Automatisez l'
              </motion.span>
              <motion.span
                variants={wordVariants}
                className="inline-block mr-2 bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent font-semibold"
              >
                onboarding
              </motion.span>
              <motion.span
                variants={wordVariants}
                className="inline-block mr-2"
              >
                et l'
              </motion.span>
              <motion.span
                variants={wordVariants}
                className="inline-block mr-2 bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent font-semibold"
              >
                assistance
              </motion.span>
              <motion.span
                variants={wordVariants}
                className="inline-block"
              >
                de vos équipes
              </motion.span>
            </motion.div>
          </motion.div>

          {/* Sous-titre */}
          <motion.p
            variants={fadeInUp}
            initial="hidden"
            animate="show"
            className="text-xl sm:text-2xl text-gray-600 mb-8 leading-relaxed max-w-3xl mx-auto"
          >
            Simplifiez l'intégration de vos collaborateurs, réduisez la charge des équipes RH, 
            et offrez un support 24/7 à votre entreprise.
          </motion.p>

          {/* Bouton CTA */}
          <motion.div
            variants={fadeInScale}
            initial="hidden"
            animate="show"
            className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-16"
          >
            <a
              href="/chat"
              className="group inline-flex items-center space-x-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white px-8 py-4 rounded-2xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 active:scale-95"
            >
              <SparklesIcon className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
              <span>Essayer maintenant</span>
            </a>
            
            <a
              href="/contact"
              className="group inline-flex items-center space-x-3 bg-white/80 backdrop-blur-sm text-gray-700 px-8 py-4 rounded-2xl font-semibold text-lg border border-gray-200/60 hover:border-violet-300 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 active:scale-95"
            >
              <svg className="w-5 h-5 text-violet-600 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a1 1 0 001.41 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>Nous contacter</span>
            </a>
          </motion.div>

          {/* Indicateur de scroll - maintenant en dessous des boutons */}
          <motion.div
            variants={{ 
              hidden: { opacity: 0 }, 
              show: { opacity: 1, transition: { delay: 1.2, duration: 0.5 } } 
            }}
            initial="hidden"
            animate="show"
            className="flex flex-col items-center space-y-2 text-gray-400"
          >
            <span className="text-sm font-medium">Découvrir ConnectRH</span>
            <ArrowDownIcon className="w-5 h-5 animate-bounce" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
